import { useCallback } from 'react';

import { retryFailedQueries } from '@/components/retryFailedQueries';
import SomethingHappened from '@/components/SomethingHappened';
import { usePermissions } from '@/contexts/auth';
import { useCage, useCageTimezone } from '@/contexts/cage';
import { useGetCageBioscopeImages } from '@/domain/bioscopeImages';
import { useGetCageStatus } from '@/domain/cages';
import { useCommitImageFeedback } from '@/domain/imageFeedback';
import { bioscopeImageMapper } from '@/domain/images';
import { useImageState } from '@/hooks/image-state';
import useQueryParams from '@/hooks/useQueryParams';
import type { CagePageInterface } from '@/routes/Client/Cage/utils/cage-pages-interface';

import CageBioscopeInspectionPageContent from './CageBioscopeInspectionPageContent';

export default function CageBioscopeInspectionContainer({
    clientId,
    locationId,
    cageId,
}: CagePageInterface) {
    const { dataAvailability } = useCage();
    const timezone = useCageTimezone();
    const hasColorWelfare = dataAvailability?.hasColorWelfare;
    const { isOptoScaleAdmin } = usePermissions();
    const [queryParams] = useQueryParams();
    const permalinkImageId =
        typeof queryParams.bioscopeImageId === 'string' ? queryParams.bioscopeImageId : undefined;

    const cageStatusQuery = useGetCageStatus(cageId, { staleTime: 60 * 1000 });
    const { isLoading: cageStatusIsLoading, data: cageStatus } = cageStatusQuery;

    const {
        images,
        days,
        isLoading: bioscopeImagesLoading,
        isPreviousData,
        isError: bioscopeImagesError,
        isFetching: bioscopeImagesFetching,
        refetch: refetchBioscopeImages,
        onDayChange,
    } = useGetCageBioscopeImages(cageId ?? 0, permalinkImageId);

    const bioscopeImageState = useImageState({
        loading: bioscopeImagesLoading && images.length === 0,
        isFetching: isPreviousData,
        data: images,
        dayswithimages: days,
        onDayChange,
        mapper: useCallback(bioscopeImageMapper(cageId), [cageId]),
        queryParam: 'bioscopeImageId',
        timezone,
    });

    const onCommitImageFeedback = useCommitImageFeedback({ cageId });

    const source = hasColorWelfare ? 'color' : 'bw';

    // The page's title goes with the content, so the error state stands in as its h1.
    if (bioscopeImagesError) {
        return (
            <SomethingHappened
                headingLevel="h1"
                onRetry={refetchBioscopeImages}
                isRetrying={bioscopeImagesFetching}
            />
        );
    }

    return (
        <CageBioscopeInspectionPageContent
            clientId={clientId}
            locationId={locationId}
            cageId={cageId}
            bioscopeImageState={bioscopeImageState}
            source={source}
            isOptoScaleAdmin={isOptoScaleAdmin}
            onCommitFalseDetection={onCommitImageFeedback}
            cageStatus={cageStatus}
            cageStatusIsLoading={cageStatusIsLoading}
            cageStatusError={cageStatusQuery.error ? retryFailedQueries(cageStatusQuery) : null}
        />
    );
}
